/**
 * An app's only chrome (slice 6i §7, Paper "Apps (slice 6i, Nov 1)" B1, B2, C1,
 * C2, D1): one round ⋯ button in its top-right corner. It opens the app's
 * name, version, maker and description, then Share, Versions, All apps and,
 * for its owner, Delete. On a phone a bottom sheet whose views change in
 * place; on a wide screen a panel under the button, with Share and Versions
 * as dialogs.
 */
import { Button } from "@crew/ui/components/button";
import { Checkbox } from "@crew/ui/components/checkbox";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@crew/ui/components/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@crew/ui/components/dialog";
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from "@crew/ui/components/drawer";
import { Link } from "@crew/ui/components/link";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { cn } from "@crew/ui/lib/utils";
import {
  Check,
  ChevronRight,
  Clock,
  Ellipsis,
  LayoutGrid,
  Lock,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { errorHint } from "../../app/api";
import { usePeople } from "../people/api";
import {
  useApp,
  useDeleteApp,
  useMakeLive,
  useShareApp,
  type AppItem,
  type AppSharing,
} from "./api";

type View = "menu" | "share" | "versions";

const SHARING_WORDS: Record<AppSharing, string> = {
  owner: "Just you",
  people: "People you choose",
  crew: "Everyone in the crew",
};

// fallow-ignore-next-line complexity -- a screen: checked in the browser at 390 and 1440 px and by e2e/apps.spec.ts, not unit tests
export function AppMenu({ app }: { app: AppItem }) {
  const mobile = useIsMobile();
  const [view, setView] = useState<View | null>(null);
  const [deleting, setDeleting] = useState(false);
  const close = () => setView(null);
  const body =
    view === "share" ? (
      <ShareBody app={app} onDone={close} />
    ) : view === "versions" ? (
      <VersionsBody app={app} />
    ) : (
      <MenuBody
        app={app}
        onShare={() => setView("share")}
        onVersions={() => setView("versions")}
        onDelete={() => {
          close();
          setDeleting(true);
        }}
      />
    );
  return (
    <>
      <button
        type="button"
        aria-label="About this app"
        data-app-menu
        onClick={() => setView(view ? null : "menu")}
        className="absolute top-3 right-3 z-20 flex size-10 items-center justify-center rounded-full bg-card text-foreground shadow-[0_1px_3px_rgb(31_42_68/14%)] hover:bg-muted"
      >
        <Ellipsis className="size-[18px]" />
      </button>
      {mobile ? (
        <Drawer open={view !== null} onOpenChange={(o) => !o && close()} showSwipeHandle>
          <DrawerContent className="px-5 pt-2 pb-7">
            <DrawerTitle className="sr-only">{app.title}</DrawerTitle>
            <DrawerDescription className="sr-only">This app's menu</DrawerDescription>
            {body}
          </DrawerContent>
        </Drawer>
      ) : view === "menu" ? (
        <>
          <button
            type="button"
            aria-label="Close the menu"
            className="fixed inset-0 z-20 cursor-default"
            onClick={close}
          />
          <div
            data-app-menu-panel
            className="absolute top-[60px] right-3 z-30 w-[360px] rounded-[14px] border border-border bg-card px-[18px] pt-[18px] pb-2 shadow-[0_12px_32px_rgb(31_42_68/12%)]"
          >
            {body}
          </div>
        </>
      ) : (
        <Dialog open={view !== null} onOpenChange={(o) => !o && close()}>
          <DialogContent className="sm:max-w-[520px]">
            <DialogHeader className="sr-only">
              <DialogTitle>{app.title}</DialogTitle>
              <DialogDescription>{view === "share" ? "Share" : "Versions"}</DialogDescription>
            </DialogHeader>
            {body}
          </DialogContent>
        </Dialog>
      )}
      <DeleteApp app={app} open={deleting} onOpenChange={setDeleting} />
    </>
  );
}

/** One of the menu's rows: an icon, a label, an optional value and chevron. */
// fallow-ignore-next-line complexity -- a screen: checked in the browser at 390 and 1440 px and by e2e/apps.spec.ts, not unit tests
function Row({
  icon,
  label,
  value,
  danger,
  chevron = true,
  onClick,
  href,
}: {
  icon: ReactNode;
  label: string;
  value?: string;
  danger?: boolean;
  chevron?: boolean;
  onClick?: () => void;
  href?: string;
}) {
  const inner = (
    <>
      <span className="flex w-5 shrink-0 justify-center">{icon}</span>
      <span className={cn("flex-1 text-body", danger ? "text-destructive" : "text-foreground")}>
        {label}
      </span>
      {value ? <span className="text-sm text-muted-foreground">{value}</span> : null}
      {chevron ? <ChevronRight className="size-3.5 shrink-0 text-hint" /> : null}
    </>
  );
  const cls = "flex w-full items-center gap-3 border-t border-border py-3 text-left";
  return href ? (
    <Link href={href} className={cls}>
      {inner}
    </Link>
  ) : (
    <button type="button" onClick={onClick} className={cls}>
      {inner}
    </button>
  );
}

// fallow-ignore-next-line complexity -- a screen: checked in the browser at 390 and 1440 px and by e2e/apps.spec.ts, not unit tests
function MenuBody({
  app,
  onShare,
  onVersions,
  onDelete,
}: {
  app: AppItem;
  onShare: () => void;
  onVersions: () => void;
  onDelete: () => void;
}) {
  const maker = app.mine ? "for you" : `for ${app.owner.name}`;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <h2 className="font-display text-[30px] leading-[34px] text-foreground">{app.title}</h2>
        <p className="text-meta text-muted-foreground">
          Version {app.version} · made by Chief of Staff {maker}
        </p>
        {app.description ? <p className="pt-1 text-body text-copy">{app.description}</p> : null}
      </div>
      <div className="flex flex-col">
        {app.mine ? (
          <Row
            icon={<UserPlus className="size-[18px]" />}
            label="Share"
            value={SHARING_WORDS[app.sharing]}
            onClick={onShare}
          />
        ) : null}
        <Row
          icon={<Clock className="size-[18px]" />}
          label="Versions"
          value={String(app.versions)}
          onClick={onVersions}
        />
        <Row icon={<LayoutGrid className="size-[18px]" />} label="All apps" href="/apps" />
        {app.mine ? (
          <Row
            icon={<Trash2 className="size-[18px] text-destructive" />}
            label="Delete app"
            danger
            chevron={false}
            onClick={onDelete}
          />
        ) : null}
      </div>
    </div>
  );
}

/** Who can open it: just you, people you choose, or everyone in the crew (slice 6i §5.2). */
// fallow-ignore-next-line complexity -- a screen: checked in the browser at 390 and 1440 px and by e2e/apps.spec.ts, not unit tests
function ShareBody({ app, onDone }: { app: AppItem; onDone: () => void }) {
  const people = usePeople();
  const share = useShareApp(app.id);
  const [sharing, setSharing] = useState<AppSharing>(app.sharing);
  const [chosen, setChosen] = useState<Set<string>>(new Set(app.people));
  const others = (people.data?.people ?? []).filter((p) => p.id !== app.owner.id);
  const choice = (s: AppSharing, icon: ReactNode) => (
    <button
      type="button"
      data-sharing={s}
      onClick={() => setSharing(s)}
      className="flex w-full items-center gap-3 border-border px-3.5 py-3 text-left not-first:border-t"
    >
      <span className="flex w-[18px] shrink-0 justify-center text-muted-foreground">{icon}</span>
      <span
        className={cn(
          "flex-1 text-body",
          sharing === s ? "font-medium text-foreground" : "text-copy",
        )}
      >
        {SHARING_WORDS[s]}
      </span>
      <span className="flex w-[18px] shrink-0 justify-center">
        {sharing === s ? <Check className="size-[18px] text-foreground" /> : null}
      </span>
    </button>
  );
  const none = sharing === "people" && chosen.size === 0;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold text-foreground">Share {app.title}</h2>
        <p className="text-sm text-muted-foreground">
          Who can open it. Only you can change it or share it on.
        </p>
      </div>
      <div className="flex flex-col rounded-xl bg-muted">
        {choice("owner", <Lock className="size-4" />)}
        {choice("people", <UserPlus className="size-4" />)}
        {choice("crew", <Users className="size-4" />)}
      </div>
      {sharing === "people" ? (
        <div className="flex flex-col">
          <p className="pb-1 text-xs tracking-[0.06em] text-hint uppercase">People</p>
          {others.length === 0 ? (
            <p className="py-2 text-sm text-muted-foreground">
              There's no one else in the crew yet.
            </p>
          ) : (
            others.map((p) => (
              <label key={p.id} className="flex items-center gap-3 py-2">
                <span className="flex size-[30px] shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-foreground">
                  {p.initials}
                </span>
                <span className="flex-1 text-body text-foreground">{p.name}</span>
                <Checkbox
                  checked={chosen.has(p.id)}
                  onCheckedChange={() =>
                    setChosen((was) => {
                      const next = new Set(was);
                      if (next.has(p.id)) next.delete(p.id);
                      else next.add(p.id);
                      return next;
                    })
                  }
                />
              </label>
            ))
          )}
        </div>
      ) : null}
      {share.error ? (
        <p role="alert" className="text-meta text-destructive">
          {errorHint(share.error)}
        </p>
      ) : null}
      <div className="flex flex-col gap-1.5 sm:flex-row-reverse sm:gap-2">
        <Button
          size="lg"
          disabled={none || share.isPending}
          onClick={() =>
            share.mutate(
              { sharing, people: sharing === "people" ? [...chosen] : [] },
              { onSuccess: onDone },
            )
          }
        >
          Share
        </Button>
        <Button size="lg" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

const day = (at: number) =>
  new Date(at).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short" });

/** Each version, newest first: the live one marked, an older one the owner can make live. */
// fallow-ignore-next-line complexity -- a screen: checked in the browser at 390 and 1440 px and by e2e/apps.spec.ts, not unit tests
function VersionsBody({ app }: { app: AppItem }) {
  const detail = useApp(app.id);
  const live = useMakeLive(app.id);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold text-foreground">Versions of {app.title}</h2>
        <p className="text-sm text-muted-foreground">
          Each change the crew made. What people entered stays with every version.
        </p>
      </div>
      <div className="flex flex-col">
        {/* fallow-ignore-next-line complexity -- a version's row: checked in the browser and by e2e/apps.spec.ts */}
        {(detail.data?.versions ?? []).map((v) => (
          <div
            key={v.version}
            data-version={v.version}
            className="flex items-start gap-3 border-t border-border py-3.5"
          >
            <span
              className={cn(
                "w-7 shrink-0 font-mono text-meta leading-5",
                v.live ? "text-foreground" : "text-muted-foreground",
              )}
            >
              v{v.version}
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <p className={cn("text-body text-foreground", v.live && "font-medium")}>
                {v.message}
              </p>
              <p className="text-meta text-muted-foreground">
                {day(v.at)} · {v.author}
              </p>
            </div>
            {v.live ? (
              <span className="flex shrink-0 items-center gap-1.5 pt-0.5 text-meta text-moss">
                <span className="size-[7px] rounded-full bg-moss" />
                Live
              </span>
            ) : app.mine ? (
              <Button
                size="sm"
                variant="outline"
                disabled={live.isPending}
                onClick={() => live.mutate(v.version)}
              >
                Make live
              </Button>
            ) : null}
          </div>
        ))}
      </div>
      <p className="border-t border-border pt-2.5 text-meta text-hint">
        To change it, ask the crew in a chat: "In {app.title}, …"
      </p>
    </div>
  );
}

// fallow-ignore-next-line complexity -- a screen: checked in the browser at 390 and 1440 px and by e2e/apps.spec.ts, not unit tests
function DeleteApp({
  app,
  open,
  onOpenChange,
}: {
  app: AppItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const deleting = useDeleteApp(app.id);
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {app.title} for good?</AlertDialogTitle>
          <AlertDialogDescription>
            What people entered in it, and every version, go with it. The crew can't get it back.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {deleting.error ? (
          <p role="alert" className="text-meta text-destructive">
            {errorHint(deleting.error)}
          </p>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel>Keep it</AlertDialogCancel>
          <Button
            variant="destructive"
            disabled={deleting.isPending}
            data-app-delete-confirm
            onClick={() =>
              deleting.mutate(undefined, {
                onSuccess: () => void navigate("/apps", { replace: true }),
              })
            }
          >
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
